import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import {
  ArrowLeft, TrendingUp, TrendingDown, Minus, Flame,
  Dumbbell, Brain, Heart, Gamepad2, BookOpen, Sparkles,
  Calendar, Zap
} from "lucide-react";
import { devError } from "../utils/devLogger";

const TrendBadge = ({ change, metric }) => {
  if (change === 0) return null;
  const isUp = change > 0;
  const Icon = isUp ? TrendingUp : TrendingDown;
  const color = isUp ? "text-green-600 bg-green-50" : "text-red-500 bg-red-50";
  // For stress, lower is better (inverted)
  const effectiveColor = metric === "stress" ? (isUp ? "text-red-500 bg-red-50" : "text-green-600 bg-green-50") : color;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${effectiveColor}`}>
      <Icon className="w-3 h-3" />
      {Math.abs(change).toFixed(1)}
    </span>
  );
};

const WeeklyDigest = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDigest = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/digest/weekly`);
      setDigest(res.data.digest);
    } catch (error) {
      devError("Failed to fetch digest:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDigest();
  }, [fetchDigest]);

  if (loading) {
    return (
      <div className="min-h-screen bg-sky-50 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-sky-400/30 glow-pulse" />
      </div>
    );
  }

  const metrics = digest ? [
    { key: "mood", label: "Mood", value: digest.averages.mood, prev: digest.prev_averages.mood },
    { key: "energy", label: "Energy", value: digest.averages.energy, prev: digest.prev_averages.energy },
    { key: "focus", label: "Focus", value: digest.averages.focus, prev: digest.prev_averages.focus },
    { key: "stress", label: "Stress", value: digest.averages.stress, prev: digest.prev_averages.stress },
    { key: "sleep", label: "Sleep", value: digest.averages.sleep, prev: digest.prev_averages.sleep },
  ] : [];

  return (
    <div className="min-h-screen bg-sky-50" data-testid="weekly-digest-page">
      <div className="fixed top-0 right-0 w-96 h-96 bg-sky-300/15 rounded-full blur-3xl pointer-events-none" />

      <header className="fixed top-0 w-full z-50 glass-header">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-sky-600" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-sky-400" strokeWidth={1.5} />
            <span className="text-sm text-sky-700">Weekly Digest</span>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-3xl mx-auto space-y-6">
          {!digest ? (
            <div className="glass-card p-12 text-center">
              <p className="text-slate-500">No data available yet. Complete some activities first!</p>
            </div>
          ) : (
            <>
              {/* Period Header */}
              <div className="text-center">
                <p className="text-xs uppercase tracking-[0.2em] text-sky-500 mb-1">Your Week</p>
                <h1 className="text-3xl font-light text-sky-900" style={{ fontFamily: "Outfit" }}>
                  Weekly Digest
                </h1>
                <p className="text-sm text-slate-500 mt-1">
                  {new Date(digest.period.start + "T12:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  {" — "}
                  {new Date(digest.period.end + "T12:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                </p>
              </div>

              {/* Score + Activity Summary */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="glass-card p-5 text-center" data-testid="score-card">
                  <Sparkles className="w-6 h-6 text-sky-400 mx-auto mb-2" strokeWidth={1.5} />
                  <p className="text-3xl font-light text-sky-900" style={{ fontFamily: "Outfit" }}>
                    {Math.round(digest.score.overall)}
                  </p>
                  <p className="text-xs text-slate-500">Body & Mind Score</p>
                </div>
                <div className="glass-card p-5 text-center" data-testid="streak-card">
                  <Flame className="w-6 h-6 text-orange-400 mx-auto mb-2" strokeWidth={1.5} />
                  <p className="text-3xl font-light text-sky-900" style={{ fontFamily: "Outfit" }}>
                    {digest.streak.current}
                  </p>
                  <p className="text-xs text-slate-500">Current Streak</p>
                </div>
                <div className="glass-card p-5 text-center" data-testid="active-days-card">
                  <Calendar className="w-6 h-6 text-green-400 mx-auto mb-2" strokeWidth={1.5} />
                  <p className="text-3xl font-light text-sky-900" style={{ fontFamily: "Outfit" }}>
                    {digest.days_active}<span className="text-lg text-sky-300">/7</span>
                  </p>
                  <p className="text-xs text-slate-500">Days Active</p>
                </div>
              </div>

              {/* Activity Breakdown */}
              <div className="glass-card p-6" data-testid="activity-breakdown">
                <h3 className="text-lg font-medium text-sky-800 mb-4" style={{ fontFamily: "Outfit" }}>
                  Activity Breakdown
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                  {[
                    { icon: Dumbbell, label: "Physical", value: digest.stats.physical_exercises, prev: digest.stats.prev_physical, color: "text-cyan-500" },
                    { icon: Brain, label: "Cognitive", value: digest.stats.cognitive_exercises, prev: digest.stats.prev_cognitive, color: "text-indigo-500" },
                    { icon: Heart, label: "Med. Min", value: digest.stats.meditation_minutes, prev: null, color: "text-pink-500" },
                    { icon: Gamepad2, label: "Games", value: digest.stats.games_played, prev: null, color: "text-amber-500" },
                    { icon: BookOpen, label: "Journal", value: digest.stats.journal_entries, prev: null, color: "text-green-500" },
                  ].map(item => (
                    <div key={item.label} className="text-center">
                      <item.icon className={`w-5 h-5 mx-auto mb-1 ${item.color}`} strokeWidth={1.5} />
                      <p className="text-xl font-semibold text-sky-900" style={{ fontFamily: "Outfit" }}>{item.value}</p>
                      <p className="text-xs text-slate-500">{item.label}</p>
                      {item.prev !== null && item.prev !== undefined && (
                        <p className="text-xs text-slate-400 mt-0.5">
                          vs {item.prev} last week
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Wellness Metrics */}
              <div className="glass-card p-6" data-testid="wellness-metrics">
                <h3 className="text-lg font-medium text-sky-800 mb-4" style={{ fontFamily: "Outfit" }}>
                  Wellness Metrics
                </h3>
                <div className="space-y-4">
                  {metrics.map(m => {
                    const change = m.prev > 0 ? m.value - m.prev : 0;
                    return (
                      <div key={m.key} className="flex items-center gap-4">
                        <span className="text-sm text-sky-700 w-16">{m.label}</span>
                        <div className="flex-1">
                          <Progress value={m.value * 10} className="h-2.5 bg-sky-100" />
                        </div>
                        <span className="text-sm font-medium text-sky-900 w-10 text-right">{m.value}</span>
                        <div className="w-16">
                          <TrendBadge change={change} metric={m.key} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Trends */}
              {digest.trends.length > 0 && (
                <div className="glass-card p-6" data-testid="trends-section">
                  <h3 className="text-lg font-medium text-sky-800 mb-4" style={{ fontFamily: "Outfit" }}>
                    Notable Trends
                  </h3>
                  <div className="space-y-3">
                    {digest.trends.map(trend => {
                      const Icon = trend.direction === "up" ? TrendingUp : TrendingDown;
                      const isPositive = trend.metric === "stress" ? trend.direction === "down" : trend.direction === "up";
                      return (
                        <div key={trend.metric} className={`flex items-center gap-3 p-3 rounded-xl ${isPositive ? "bg-green-50 border border-green-100" : "bg-amber-50 border border-amber-100"}`}>
                          <Icon className={`w-5 h-5 ${isPositive ? "text-green-500" : "text-amber-500"}`} strokeWidth={1.5} />
                          <div>
                            <p className="text-sm font-medium text-sky-800 capitalize">{trend.metric}</p>
                            <p className="text-xs text-slate-500">
                              {trend.last_week} → {trend.this_week} ({trend.change > 0 ? "+" : ""}{trend.change})
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Highlights */}
              <div className="glass-card p-6" data-testid="highlights-section">
                <h3 className="text-lg font-medium text-sky-800 mb-4" style={{ fontFamily: "Outfit" }}>
                  Highlights
                </h3>
                <div className="space-y-2">
                  {digest.highlights.map((h, i) => (
                    <div key={`highlight-${i}-${h.slice(0, 15)}`} className="flex items-start gap-2">
                      <Zap className="w-4 h-4 text-sky-400 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                      <p className="text-sm text-slate-600">{h}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Motivational Nudge */}
              <div className="glass-card p-6 border-l-4 border-sky-400 bg-gradient-to-r from-sky-50/50 to-transparent" data-testid="nudge-section">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-sky-400/20 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-sky-500" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-sky-800 mb-1">This week's nudge</h4>
                    <p className="text-slate-600">{digest.nudge}</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default WeeklyDigest;
